// Which assessment and class the Teaching screens are looking at (R13).
// Both live in the URL (?a=, ?class=) so back/refresh/share keep them; the
// last-used assessment is remembered per teacher in localStorage.

import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { useLocation, useSearch } from 'wouter-preact';
import { api, me, type AssessmentRow, type Dataset } from './teacher-api';
import { analyse, type Analysis, type ClassFilter } from '@teacher-shared/analysis';

const lastKey = () => `tos:last-assessment:${me.value?.id ?? 'anon'}`;
function readLast(): string | null { try { return localStorage.getItem(lastKey()); } catch { return null; } }
function writeLast(id: string): void { try { localStorage.setItem(lastKey(), id); } catch { /* private mode */ } }

/** Build a Teaching link that keeps the assessment and class context. */
export function teachHref(path: string, ctx: { a?: string | null; class?: ClassFilter | null } = {}): string {
  const p = new URLSearchParams();
  if (ctx.a) p.set('a', ctx.a);
  if (ctx.class && ctx.class !== 'all') p.set('class', ctx.class);
  const q = p.toString();
  return q ? `${path}?${q}` : path;
}

export function studentHref(a: string, studentId: string, cls?: ClassFilter): string {
  return teachHref(`/teaching/a/${a}/student/${studentId}`, { class: cls });
}

// ── Year filter (slice 4 §05): All or one year level, remembered per teacher ──

const yearKey = () => `tos:year:${me.value?.id ?? 'anon'}`;
function readYear(): number | null {
  try { const v = localStorage.getItem(yearKey()); return v ? Number(v) || null : null; } catch { return null; }
}
const yearSig = signal<Record<string, number | null>>({});
/** The Teaching header's year filter; null = All. */
export function yearFilter(): number | null {
  const who = me.value?.id ?? 'anon';
  return who in yearSig.value ? yearSig.value[who] : readYear();
}
export function setYearFilter(year: number | null): void {
  yearSig.value = { ...yearSig.value, [me.value?.id ?? 'anon']: year };
  try { if (year === null) localStorage.removeItem(yearKey()); else localStorage.setItem(yearKey(), String(year)); } catch { /* private mode */ }
}
/** Tests in the chosen year (all when the filter is All). */
export function inYear(list: AssessmentRow[]): AssessmentRow[] {
  const y = yearFilter();
  return y === null ? list : list.filter((x) => x.year_level === y);
}

/** The cached dataset for a test, if loaded (banner, finalised lock). */
export function datasetOf(a: string): Dataset | undefined {
  return cache.value[keyOf(a)]?.dataset;
}

// ── Assessment list ──────────────────────────────────────────────────

export const assessments = signal<AssessmentRow[] | null>(null);
export async function refreshAssessments(): Promise<AssessmentRow[]> {
  const list = await api<AssessmentRow[]>('GET', '/api/t/assessments');
  assessments.value = list;
  return list;
}

// ── Dataset cache keyed by teacher + assessment ──────────────────────

interface Entry { status: 'loading' | 'ready' | 'error'; analysis?: Analysis; dataset?: Dataset; error?: string }
const cache = signal<Record<string, Entry>>({});
const keyOf = (a: string) => `${me.value?.id}:${a}`;

export async function loadDataset(a: string, force = false): Promise<void> {
  const k = keyOf(a);
  if (!force && cache.value[k]?.status === 'ready') return;
  if (!cache.value[k]) cache.value = { ...cache.value, [k]: { status: 'loading' } };
  try {
    const ds = await api<Dataset>('GET', `/api/t/assessments/${a}/dataset`);
    cache.value = { ...cache.value, [k]: { status: 'ready', dataset: ds, analysis: analyse(ds) } };
  } catch (e) {
    cache.value = { ...cache.value, [k]: { status: 'error', error: (e as Error).message } };
  }
}
export function invalidateDataset(a: string): void {
  const { [keyOf(a)]: _, ...rest } = cache.value;
  cache.value = rest;
}

/** Current assessment id: ?a=, else the remembered one, else the newest
 *  analysed/marking test. Writes the choice back into the URL. */
export function useAssessmentId(routeA?: string): string | null | undefined {
  const search = useSearch();
  const [loc, navigate] = useLocation();
  const fromUrl = routeA ?? new URLSearchParams(search).get('a');
  const list = assessments.value;
  useEffect(() => { if (!list) void refreshAssessments(); }, [list]);
  let id: string | null | undefined = fromUrl;
  if (!id && list) {
    const last = readLast();
    const shown = inYear(list);
    id = (last && shown.find((x) => x.id === last)?.id) ?? shown.find((x) => x.stage !== 'setup')?.id ?? shown[0]?.id ?? null;
  }
  useEffect(() => {
    if (id) writeLast(id);
    if (id && !fromUrl) {
      const p = new URLSearchParams(search);
      p.set('a', id);
      navigate(`${loc}?${p}`, { replace: true });
    }
  }, [id, fromUrl]);
  return list === null && !fromUrl ? undefined : id;
}

export type DataState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; a: string; A: Analysis; ds: Dataset };

export function useAnalysis(routeA?: string): DataState {
  const a = useAssessmentId(routeA);
  useEffect(() => { if (a) void loadDataset(a); }, [a]);
  if (a === undefined) return { kind: 'loading' };
  if (a === null) return { kind: 'empty' };
  const e = cache.value[keyOf(a)];
  if (!e || e.status === 'loading') return { kind: 'loading' };
  if (e.status === 'error') return { kind: 'error', message: e.error ?? 'Could not load' };
  return { kind: 'ready', a, A: e.analysis!, ds: e.dataset! };
}

/** Class filter from ?class=; anything outside the test's classes is All. */
export function useClass(classes: string[]): [ClassFilter, (c: ClassFilter) => void] {
  const search = useSearch();
  const [loc, navigate] = useLocation();
  const raw = new URLSearchParams(search).get('class');
  const cls = raw && classes.includes(raw) ? raw : 'all';
  const set = (c: ClassFilter) => {
    const p = new URLSearchParams(search);
    if (c === 'all') p.delete('class'); else p.set('class', c);
    const q = p.toString();
    navigate(q ? `${loc}?${q}` : loc);
  };
  return [cls, set];
}
